/**
 * Phone numbers in E.164, which is the only form a carrier accepts.
 *
 * Deliberately not a full libphonenumber: that library is larger than this
 * entire Worker bundle, and the job here is narrow — take what an estimator
 * typed into a phone field and decide whether it is a number we can text.
 *
 * The rules are therefore explicit about what they do and do not know:
 *
 *   * anything already written in international form (`+…`, or the `00` prefix
 *     most of the world dials instead) is accepted as-is, because the person
 *     typing it has said what they mean;
 *   * North American numbers are fully handled, since that is where this app's
 *     defaults live (US country code, USD, America/New_York);
 *   * for the other countries listed below, a national number with the usual
 *     `0` trunk prefix is converted;
 *   * everything else returns null rather than guessing. A wrong guess texts a
 *     stranger, so refusing is the cheaper error.
 *
 * `null` never means "bad customer data" on its own — plenty of customers have
 * a landline on file. It means "not a number this app will text", and the
 * caller treats that as "no SMS", never as a validation failure on the record.
 */

/** Longest and shortest an E.164 subscriber number can be, per the standard. */
const MIN_E164_DIGITS = 8;
const MAX_E164_DIGITS = 15;

/**
 * Country calling codes this module can build from a *national* number.
 *
 * `trunkPrefix` is the digit a national number carries and the international
 * form drops — '0' across most of Europe and Oceania. The NANP has no trunk
 * prefix; its optional leading '1' is the country code itself, handled below.
 *
 * `nationalDigits` is how long the national number is once any trunk prefix is
 * off, as an inclusive [min, max]. It exists for one decision — whether digits
 * that open with the country's own dial code are a national number or the
 * international form written without its `+` — and a length is the only thing
 * that can tell those apart (see `readsAsInternational`). The ranges are the
 * regulators' own published number lengths:
 *
 *   * GB 9-10 (Ofcom: ten digits, bar a few nine-digit ranges such as 016977),
 *   * IE 7-9 (ComReg: nine for mobiles, fewer in some geographic areas),
 *   * AU 9 and ZA 9 and FR 9 and NL 9 and ES 9 — each a single fixed length
 *     for every number in the plan,
 *   * NZ 8-10 (eight geographic, mobiles up to ten),
 *   * IT 6-11: Italy keeps the leading 0 as part of the number rather than as
 *     a trunk prefix, and its ranges genuinely vary this widely,
 *   * NANP 10, which the branch below handles on its own.
 *
 * `null` means this module cannot state the length, and the dial-code reading
 * falls back to the older prefix-only guess for that country. Germany is the
 * one such entry: its national numbers run from about six digits to thirteen
 * with no dependable range, and a guessed one here would be worse than the
 * ambiguity it replaced.
 */
type NationalFormat = {
  dialCode: string;
  trunkPrefix: string | null;
  nationalDigits: readonly [number, number] | null;
};

const NATIONAL_FORMATS: Readonly<Record<string, NationalFormat>> = {
  US: { dialCode: '1', trunkPrefix: null, nationalDigits: [10, 10] },
  CA: { dialCode: '1', trunkPrefix: null, nationalDigits: [10, 10] },
  PR: { dialCode: '1', trunkPrefix: null, nationalDigits: [10, 10] },
  GB: { dialCode: '44', trunkPrefix: '0', nationalDigits: [9, 10] },
  IE: { dialCode: '353', trunkPrefix: '0', nationalDigits: [7, 9] },
  AU: { dialCode: '61', trunkPrefix: '0', nationalDigits: [9, 9] },
  NZ: { dialCode: '64', trunkPrefix: '0', nationalDigits: [8, 10] },
  ZA: { dialCode: '27', trunkPrefix: '0', nationalDigits: [9, 9] },
  FR: { dialCode: '33', trunkPrefix: '0', nationalDigits: [9, 9] },
  DE: { dialCode: '49', trunkPrefix: '0', nationalDigits: null },
  NL: { dialCode: '31', trunkPrefix: '0', nationalDigits: [9, 9] },
  ES: { dialCode: '34', trunkPrefix: null, nationalDigits: [9, 9] },
  IT: { dialCode: '39', trunkPrefix: null, nationalDigits: [6, 11] },
};

/** Countries on the North American Numbering Plan, where 10 digits is national. */
const NANP_DIAL_CODE = '1';
const NANP_NATIONAL_DIGITS = 10;

/**
 * Converts a typed phone number to E.164, or returns null if this module
 * cannot do so safely.
 *
 * `defaultCountry` is the ISO 3166-1 alpha-2 code to assume for a number
 * written in national form — in practice the company's own country, since a
 * cleaning company's customers are within driving distance of it.
 */
export function normalizePhoneToE164(raw: string | null | undefined, defaultCountry = 'US'): string | null {
  if (!raw) return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Extensions are for switchboards, not handsets: a number carrying one is
  // not a mobile we should be texting.
  //
  // The `x` form deliberately anchors on nothing. Word boundaries are useless
  // here in both directions: in "x123" the x and the digit are both word
  // characters, and in "02079460958x123" so are the 8 and the x — so any \b
  // placed around it fails on one form or the other, and the extension digits
  // get folded into the subscriber number. "020 7946 0958 x123" would become
  // +442079460958123: the right length to look plausible, belonging to nobody.
  // An x followed by digits has no other meaning in a phone field.
  //
  // `#` goes the same way. It is the other common extension marker — "0958
  // #123" — and it is also a DTMF key, which is the same story: digits meant to
  // be dialled *after* the call connects, not part of the number that connects
  // it. Neither belongs in an E.164 destination, so anything carrying one is
  // refused rather than silently concatenated.
  if (/\bext(?:ension|n)?\b/i.test(trimmed)) return null;
  if (/x\s*\.?\s*\d/i.test(trimmed)) return null;
  if (trimmed.includes('#')) return null;

  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return isPlausibleE164(digits) ? `+${digits}` : null;
  }

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return null;

  // "00" is what most of the world dials instead of "+", and it means exactly
  // the same thing. Read as a national number it is a disaster: under GB,
  // 0044 20 7946 0958 loses one leading zero to the trunk rule and then gets
  // the dial code prepended anyway, giving +440442079460958 — the right length
  // to pass every plausibility check, and the wrong number.
  if (digits.startsWith('00')) {
    const international = digits.slice(2);
    return isPlausibleE164(international) ? `+${international}` : null;
  }

  const format = NATIONAL_FORMATS[defaultCountry.trim().toUpperCase()];
  if (!format) return null;

  if (format.dialCode === NANP_DIAL_CODE) {
    // "555 555 0100" or "1-555-555-0100"; anything else is not a NANP number.
    const national = digits.length === NANP_NATIONAL_DIGITS + 1 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (national.length !== NANP_NATIONAL_DIGITS) return null;
    // Area codes and exchange codes both start at 2 — a leading 0 or 1 means
    // the digits are something else that happened to be ten long.
    if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(national)) return null;
    return `+${NANP_DIAL_CODE}${national}`;
  }

  const hadTrunkPrefix = Boolean(format.trunkPrefix) && digits.startsWith(format.trunkPrefix!);
  const national = hadTrunkPrefix ? digits.slice(format.trunkPrefix!.length) : digits;

  // A number written with its trunk prefix is national by definition, so the
  // dial code is always prepended. Skipping that when the remainder happens to
  // begin with the country's own digits silently eats them: the French number
  // 03 31 23 45 67 becomes 331234567 once the trunk 0 is gone, which looks
  // exactly like an international +33 number and would be stored two digits
  // short of the real one.
  const candidate =
    !hadTrunkPrefix && readsAsInternational(national, format) ? national : `${format.dialCode}${national}`;
  return isPlausibleE164(candidate) ? `+${candidate}` : null;
}

/**
 * Whether digits that open with the country's own dial code, and carried no
 * trunk prefix, are the international form written without its `+`.
 *
 * Neither the leading digits nor the presence of a trunk prefix can answer
 * this, and both readings are real:
 *
 *   * "39 340 123 4567" under IT is somebody writing +39 without the plus.
 *   * "391 234 5678" under IT is an ordinary Italian mobile — the 39x ranges
 *     are live, and Italy's dial code is 39. Read as international it would be
 *     stored as +3912345678, two digits short and belonging to nobody.
 *
 * Length is what separates them, because a country's national numbers are a
 * known digit count and the international form is that plus the dial code:
 *
 *   * stripping the dial code leaves a valid national length, and the whole
 *     string is not itself one -> international;
 *   * the whole string is a valid national length -> national;
 *   * both readings valid, or neither -> national, because somebody typing
 *     their own number in their own country is the commoner case by far and
 *     prepending a dial code that was already there produces a number that
 *     reaches nobody.
 *
 * A country with no stated length (Germany) keeps the older prefix-only guess:
 * there is nothing better to decide with, and inventing a range would be a
 * guess dressed as data.
 */
function readsAsInternational(digits: string, format: NationalFormat): boolean {
  if (!digits.startsWith(format.dialCode)) return false;
  const lengths = format.nationalDigits;
  if (!lengths) return true;
  const withinNationalLength = (value: string) => value.length >= lengths[0] && value.length <= lengths[1];
  return withinNationalLength(digits.slice(format.dialCode.length)) && !withinNationalLength(digits);
}

function isPlausibleE164(digits: string): boolean {
  if (digits.length < MIN_E164_DIGITS || digits.length > MAX_E164_DIGITS) return false;
  // Country codes never start with 0.
  return /^[1-9]\d+$/.test(digits);
}

/**
 * The digits a number stored as somebody typed it is expected to end with:
 * the subscriber number, with the country's dial code off the front.
 *
 * Phone columns hold what was typed — "(555) 555-0142", "0412 345 678",
 * "+61 412 345 678" — so a SQL suffix match is how a duplicate check finds
 * candidates before this module re-checks them properly. The suffix has to be
 * the national number: the last N digits of the E.164 form only coincide with
 * it when the dial code and the national number happen to add up to N, which
 * is true for the NANP (1 + 10) and false for, say, Australia, where a stored
 * "0412 345 678" shares only nine of the last ten digits of +61412345678 and
 * the match silently fails.
 *
 * Returns the digits unchanged when the number is not under the given
 * country's dial code at all — a foreign number on a domestic record — which
 * still matches a stored international spelling and never matches too much.
 */
export function nationalPhoneDigits(e164: string | null | undefined, defaultCountry = 'US'): string | null {
  if (!e164) return null;
  const digits = e164.replace(/\D/g, '');
  if (!digits) return null;
  const format = NATIONAL_FORMATS[defaultCountry.trim().toUpperCase()];
  if (!format || !digits.startsWith(format.dialCode)) return digits;
  return digits.slice(format.dialCode.length);
}

/**
 * Whether two numbers are the same line.
 *
 * Both sides are normalized first so a consent recorded against "+15555550100"
 * still matches a customer record edited to "(555) 555-0100" — a reformat is
 * not a new number, and treating it as one would drop consent for a cosmetic
 * edit.
 */
export function isSamePhoneNumber(
  left: string | null | undefined,
  right: string | null | undefined,
  defaultCountry = 'US',
): boolean {
  const a = normalizePhoneToE164(left, defaultCountry);
  const b = normalizePhoneToE164(right, defaultCountry);
  return a !== null && a === b;
}

/**
 * "+1••••••0100" — for structured logs, which are retained and searchable.
 *
 * The last four digits are what an operator matches against a support call;
 * the rest is not needed to answer "which send failed?".
 */
export function maskPhoneNumber(e164: string | null | undefined): string | null {
  if (!e164) return null;
  const visible = e164.slice(-4);
  const hidden = Math.max(0, e164.length - 4 - 2);
  return `${e164.slice(0, 2)}${'•'.repeat(hidden)}${visible}`;
}

/** "(555) 555-0100" for a US number, otherwise the E.164 form unchanged. */
export function formatPhoneForDisplay(e164: string | null | undefined): string {
  if (!e164) return '';
  const nanp = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return nanp ? `(${nanp[1]}) ${nanp[2]}-${nanp[3]}` : e164;
}
